/**
 * @file Client wrappers for the two ArTable point-in-time-recovery READ callables
 * (SYD-2450), the sibling of `formPitrFunctions` for a table's rows. Consumers:
 * `useFormRecoveryList`, which lists one ladder per table the form's layout holds
 * and merges them with the version's into one timeline, and `useFormRecoveryRead`,
 * which fans the same table ids out behind the moment the operator named. Nothing
 * renders from here — the hooks write `formRecoveryStore` and the sidebar and the
 * confirmation render from that.
 *
 * There is no restore wrapper here, and that is the contract rather than an
 * omission: a restore covers the form version and every table of the moment in ONE
 * transaction, so it is `restoreFormVersionTo` in `formPitrFunctions` carrying its
 * `tables`, and a per-table restore callable would be a second way to write half of
 * one.
 *
 * No try/catch anywhere in this file, exactly as in `formPitrFunctions`. A
 * rejection must reach its caller with its `code` and `details` intact — the
 * out-of-window refusal is an `invalid-argument` carrying a `FormPitrWindow`, and
 * wrapping it in `new Error(...)` flattens that into a string.
 */

import { httpsCallable } from 'firebase/functions';
import type {
  ListArTableRestorePointsRequest,
  ListArTableRestorePointsResponse,
  ReadArTableAtRequest,
  ReadArTableAtResponse,
} from '#types';
import { functions } from './firebaseFunctions';

/**
 * Lists the restore point ladder for one table under the named form version,
 * newest first.
 *
 * The server samples a fixed set of moments behind now and answers all of them at
 * once, so the request carries no cursor and no limit and the response has no page
 * to resume from. A rung the store refuses only coarsens the ladder, so the call
 * lands with a `refusedMomentCount` rather than throwing.
 *
 * @param request The form version and the table to sample.
 * @returns The whole ladder, where it ends, how many moments were refused and the
 *   reachable window.
 */
export const listArTableRestorePoints = async (
  request: ListArTableRestorePointsRequest,
): Promise<ListArTableRestorePointsResponse> => {
  const listArTableRestorePointsFn = httpsCallable<
    ListArTableRestorePointsRequest,
    ListArTableRestorePointsResponse
  >(functions, 'listArTableRestorePoints');

  const response = await listArTableRestorePointsFn(request);

  return response.data;
};

/**
 * Reads one table's rows as they stood at one moment.
 *
 * Every property of the request is required, so nothing is spread here: the
 * SYD-2445 hazard is an `undefined` reaching the wire as `null`, and this request
 * has no optional field to leave out.
 *
 * @param request The table to read and the moment, as an
 *   `ArTableRestorePoint.cursorMicros`.
 * @returns The outcome, the two digests a restore echoes back, the moment
 *   actually read and the reachable window.
 */
export const readArTableAt = async (
  request: ReadArTableAtRequest,
): Promise<ReadArTableAtResponse> => {
  const readArTableAtFn = httpsCallable<ReadArTableAtRequest, ReadArTableAtResponse>(
    functions,
    'readArTableAt',
  );

  const response = await readArTableAtFn(request);

  return response.data;
};
